import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Troubleshooting } from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutputType } from '@amzn/awscat-editor';
import { v4 as uuid } from 'uuid';
import _ from 'lodash';

import { deepClone } from '../../api/UtilsApi';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { usePendingFlushCoordinator } from '../../contexts/PendingFlushCoordinatorContext';
import { useContent } from '../v2-hooks/useContent';
import {
  listTroubleshootings,
  tryAddTroubleshooting,
  tryUpdateTroubleshooting,
  tryDeleteTroubleshooting,
  tryReorderTroubleshootings,
} from '../../services/TroubleshootingOperations';

export interface PatternTroubleshootingEditorViewModel {
  readonly troubleshootingRows: readonly Troubleshooting[];
  /** The id of the row currently in edit mode, or `undefined` when idle. */
  readonly editingRowId: string | undefined;
  readonly startEditing: (rowId: string) => void;
  readonly saveEditing: () => void;
  readonly cancelEditing: () => void;
  readonly addTroubleshootingRow: () => void;
  readonly deleteTroubleshootingRow: (rowId: string) => void;
  readonly reorderTroubleshootingRows: (newOrderIds: readonly string[]) => void;
  /** Stages a cell edit while the user types in the row being edited. */
  readonly stageFieldEdit: (
    rowId: string,
    field: 'issue' | 'solution',
    value: TiptapOutputType,
  ) => void;
}

export function usePatternTroubleshootingEditorViewModel():
  | PatternTroubleshootingEditorViewModel
  | undefined {
  const service = useContentEditorService();
  const flushCoordinator = usePendingFlushCoordinator();
  const content = useContent();

  const [editingRowId, setEditingRowId] = useState<string | undefined>(undefined);

  // Deep-cloned snapshot of ONLY the single row being edited — used to
  // restore on Cancel. Deep clone because issue/solution may be Tiptap
  // JSON objects (reference types).
  const preEditRowRef = useRef<Troubleshooting | undefined>(undefined);

  // When content changes, extract the typed rows.
  const troubleshootingRows = useMemo(
    () => (content ? listTroubleshootings(content) : []),
    [content],
  );

  // When pencil is clicked, deep-clone this row's current state.
  const startEditing = useCallback(
    (rowId: string) => {
      const row = troubleshootingRows.find((r) => r.id === rowId);
      preEditRowRef.current = row ? deepClone(row) : undefined;
      setEditingRowId(rowId);
    },
    [troubleshootingRows],
  );

  // Pending field edit — accumulates the latest value from the editor
  // so the debounced flush writes the most recent keystroke.
  const pendingFieldEditRef = useRef<
    | {
        rowId: string;
        field: 'issue' | 'solution';
        value: TiptapOutputType;
      }
    | undefined
  >(undefined);

  // Flush the pending field edit into the content tree.
  const flushFieldEdit = useCallback(() => {
    const pending = pendingFieldEditRef.current;
    if (!pending) return;
    pendingFieldEditRef.current = undefined;

    // Use service.content (live source of truth) instead of a stale
    // closure — prevents reviving a deleted row on flush-during-unmount.
    const liveContent = service.content;
    if (!liveContent) return;

    const updated = tryUpdateTroubleshooting(liveContent, pending.rowId, (row) => ({
      ...row,
      [pending.field]: pending.value,
    }));
    if (updated) service.stageEdit(updated);
  }, [service]);

  // Debounce at 2s — same cadence as SectionEditorControlViewModel.
  const debouncedFlush = useMemo(() => _.debounce(flushFieldEdit, 2000), [flushFieldEdit]);

  // Flush on unmount so in-flight keystrokes aren't dropped.
  useEffect(
    () => () => {
      debouncedFlush.flush();
    },
    [debouncedFlush],
  );

  // While mounted, let a browser-close drain stage the pending keystroke too.
  useEffect(() => {
    return flushCoordinator.registerFlusher(() => debouncedFlush.flush());
  }, [flushCoordinator, debouncedFlush]);

  // When Save is clicked, flush any pending edits then exit edit mode.
  const saveEditing = useCallback(() => {
    debouncedFlush.flush();
    preEditRowRef.current = undefined;
    setEditingRowId(undefined);
  }, [debouncedFlush]);

  // When Cancel is clicked, restore ONLY the edited row to its pre-edit
  // state. Uses current `content` (preserving changes from other parts
  // of the app) and replaces just the one row.
  const cancelEditing = useCallback(() => {
    debouncedFlush.cancel();
    pendingFieldEditRef.current = undefined;

    if (preEditRowRef.current && editingRowId) {
      const snapshot = preEditRowRef.current;
      const liveContent = service.content;
      if (liveContent) {
        const updated = tryUpdateTroubleshooting(liveContent, editingRowId, () => snapshot);
        if (updated) service.stageEdit(updated);
      }
    }

    preEditRowRef.current = undefined;
    setEditingRowId(undefined);
  }, [editingRowId, service, debouncedFlush]);

  // When "Add issue" is clicked, append an empty row and enter edit mode.
  const addTroubleshootingRow = useCallback(() => {
    const liveContent = service.content;
    if (!liveContent) return;

    const newRow = Troubleshooting.create('', '');
    newRow.id = uuid();

    const updated = tryAddTroubleshooting(liveContent, newRow);
    if (!updated) return;
    service.stageEdit(updated);

    // Enter edit mode on the new row so the user can type immediately.
    preEditRowRef.current = deepClone(newRow);
    setEditingRowId(newRow.id);
  }, [service]);

  // When the user confirms deletion of a row (View handles showing the modal).
  const deleteTroubleshootingRow = useCallback(
    (rowId: string) => {
      const liveContent = service.content;
      if (!liveContent) return;
      const updated = tryDeleteTroubleshooting(liveContent, rowId);
      if (updated) service.stageEdit(updated);

      // If we were editing the deleted row, exit edit mode.
      if (editingRowId === rowId) {
        preEditRowRef.current = undefined;
        setEditingRowId(undefined);
      }
    },
    [service, editingRowId],
  );

  // When the user types in a cell, buffer the value and schedule a
  // debounced flush.
  const stageFieldEdit = useCallback(
    (rowId: string, field: 'issue' | 'solution', value: TiptapOutputType) => {
      const prev = pendingFieldEditRef.current;
      if (prev && (prev.rowId !== rowId || prev.field !== field)) {
        debouncedFlush.flush();
      }
      pendingFieldEditRef.current = { rowId, field, value };
      debouncedFlush();
    },
    [debouncedFlush],
  );

  // When rows are reordered via drag-and-drop.
  const reorderTroubleshootingRows = useCallback(
    (newOrderIds: readonly string[]) => {
      const liveContent = service.content;
      if (!liveContent) return;
      const updated = tryReorderTroubleshootings(liveContent, newOrderIds);
      if (updated) service.stageEdit(updated);
    },
    [service],
  );

  // A parent-teardown render can transiently clear content before this view
  // unmounts; return undefined so the view renders null instead of throwing.
  if (!content) return undefined;

  return {
    troubleshootingRows,
    editingRowId,
    startEditing,
    saveEditing,
    cancelEditing,
    addTroubleshootingRow,
    deleteTroubleshootingRow,
    reorderTroubleshootingRows,
    stageFieldEdit,
  };
}
